/**
 * Лента оценки интерьеров на телефоне: чистая логика очереди и типы ответов `/api/interiors/*`
 * (спека §2, §5). Модуль без runtime-импортов: его напрямую грузит `node --test`; запросы — в
 * `interiorsApi.ts`.
 *
 * Очередь держит пару фото вперёд (`needMore`), последний голос — в `last` для отмены (отмена
 * только одного шага), `done` — сервер сказал, что в пуле больше нет.
 */

export type Verdict = "works" | "fails";

export const SHARE_URL = "https://reels.uni-renter.ru/m/interiors";

export type InteriorItem = { id: number; asset_path: string; tag_id: number; tag_name: string };
export type InteriorMe = { ack: boolean; voted: number; pool: number };
export type InteriorNext = { items: InteriorItem[]; left: number };

export type CategoryStat = {
  tag_id: number;
  name: string;
  pool: number;
  votes: number;
  works: number;
  fails: number;
  raters: number;
};
export type RaterStat = { user_id: number; name: string; votes: number };
export type TopItem = { id: number; asset_path: string; tag_name: string; works: number; fails: number };
export type InteriorStats = {
  categories: CategoryStat[];
  raters: RaterStat[];
  top_works: TopItem[];
  top_fails: TopItem[];
};

export type Queue = {
  items: InteriorItem[];
  last: { item: InteriorItem; verdict: Verdict } | null;
  /** id, уже побывавшие головой и оценённые в этой сессии: опоздавший ответ сервера их не вернёт. */
  seen: number[];
  done: boolean;
};

/** Голос отдан: голова уходит в `last` и в `seen`, хвост остаётся. Пустая очередь не меняется. */
export function advance(q: Queue, verdict: Verdict): Queue {
  const [head, ...rest] = q.items;
  if (!head) return q;
  return { ...q, items: rest, last: { item: head, verdict }, seen: [...q.seen, head.id] };
}

/** Отмена последнего голоса: фото снова первое, `last` пуст (второй вызов ничего не делает). */
export function undo(q: Queue): Queue {
  if (!q.last) return q;
  return { ...q, items: [q.last.item, ...q.items], last: null };
}

/**
 * Сервер ответил `NOT_IN_POOL` на голос: фото вышло из пула, отменять нечего. Голос уходит в
 * фоне после `advance`, поэтому такое фото уже в `last`, а не в голове — голову не трогаем.
 */
export function forget(q: Queue, id: number): Queue {
  return q.last?.item.id === id ? { ...q, last: null } : q;
}

export function needMore(q: Queue): boolean {
  return q.items.length < 2 && !q.done;
}

/**
 * Дописать подгруженное без дублей (очередь, `seen`, `last`). Сервер уже исключает очередь, `last` и
 * голоса человека, поэтому ответ без нового — конец: `done` ставится и не сбрасывается.
 */
export function merge(q: Queue, fetched: InteriorItem[], _left: number): Queue {
  const known = new Set([...q.items.map((i) => i.id), ...q.seen, ...(q.last ? [q.last.item.id] : [])]);
  const fresh = fetched.filter((i) => !known.has(i.id));
  return { ...q, items: [...q.items, ...fresh], done: q.done || fresh.length === 0 };
}

/** Тег последнего фото в очереди: сервер продолжает подборку с него (без последовательных повторов). */
export function afterTag(q: Queue): number | null {
  const tail = q.items[q.items.length - 1] ?? q.last?.item;
  return tail ? tail.tag_id : null;
}

/** Что уже на руках (очередь и последний голос): сервер не должен присылать это снова. */
export function excludeIds(q: Queue): number[] {
  return [...q.items.map((i) => i.id), ...(q.last ? [q.last.item.id] : [])];
}

/** Доля 👍 в категории; без голосов — null. */
function share(c: CategoryStat): number | null {
  return c.votes > 0 ? c.works / c.votes : null;
}

/** Категории по убыванию доли 👍 (ничья — больше голосов); без голосов — в конце. Исходный массив не меняется. */
export function sortByShare(cats: CategoryStat[]): CategoryStat[] {
  return [...cats].sort((a, b) => {
    const sa = share(a);
    const sb = share(b);
    if (sa === null || sb === null) return sa === sb ? 0 : sa === null ? 1 : -1;
    return sb - sa || b.votes - a.votes;
  });
}

/** Доля 👍 целыми процентами; «—», если голосов нет. */
export function sharePct(c: CategoryStat): string {
  const s = share(c);
  return s === null ? "—" : `${Math.round(s * 100)}%`;
}
